import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Plus, Trash2, Pencil, X, CheckCircleIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../ui/sheet';

const QuizEditor = ({
  editingQuizMaterial,
  disabled,
  quizData,
  onUpdateQuiz,
  addQuestion,
  editQuestion: editQuestionProp,
  onClose,
  onSave,
  isSaving = false,
  setIsSaving,
  topics = [],
}) => {
  console.log(quizData)
  const [activeTab, setActiveTab] = useState('questions');
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  
  // Save the current set index whenever it changes
  const setCurrentSetIndexPersistent = (index) => {
    const validIndex = Math.min(Math.max(0, index), (quizData?.questionSets?.length || 1) - 1);
    setCurrentSetIndex(validIndex);
  };
  
  // Initialize quiz data with useMemo to prevent recreation on every render
  const initialQuizState = React.useMemo(() => {
    const hasQuestionSets = Array.isArray(quizData?.questionSets) && quizData.questionSets.length > 0;
    
    return {
      ...quizData,
      timelimit: 0, // Default to 0
      type: "QUIZ", // Always QUIZ
      questionSets: hasQuestionSets 
        ? quizData.questionSets.map((set, index) => ({
            id: set.id || `set-${index + 1}`,
            title: set.title || `Set ${index + 1}`, // Simple title format
            questions: Array.isArray(set.questions) ? set.questions : [],
          }))
        : [{
            id: `set-1`,
            title: 'Set 1', // Simple title format
            questions: [],
          }]
    };
  }, [quizData]);

  const [quiz, setQuiz] = useState(initialQuizState);
  // Update quiz state when quizData prop changes
  useEffect(() => {
    if (quizData) {
      const hasQuestionSets = Array.isArray(quizData.questionSets) && quizData.questionSets.length > 0;
      
      setQuiz(prevQuiz => {
        const updatedQuiz = {
          ...prevQuiz,
          ...quizData,
          timelimit: 0, // Default to 0
          type: "QUIZ", // Always QUIZ
          questionSets: hasQuestionSets 
            ? quizData.questionSets.map((set, index) => ({
                id: set.id || `set-${index + 1}`,
                title: set.title || `Set ${index + 1}`, // Simple title format
                questions: Array.isArray(set.questions) ? set.questions : [],
              }))
            : [{
                id: `set-1`,
                title: 'Set 1', // Simple title format
                questions: [],
              }]
        };
        
        // Ensure currentSetIndex is within bounds
        const validSetIndex = Math.min(currentSetIndex, updatedQuiz.questionSets.length - 1);
        if (validSetIndex !== currentSetIndex) {
          setCurrentSetIndexPersistent(validSetIndex);
        }
        
        return updatedQuiz;
      });
    }
  }, [quizData]);

  // Get the current quiz set
  const currentSet = quiz.questionSets[currentSetIndex] || quiz.questionSets[0] || {};
  console.log(currentSet)

  // Edit an existing question
  const editQuestion = (question, questionSetIndex, questionIndex) => {
    if (editQuestionProp) {
      editQuestionProp(question, questionSetIndex, questionIndex);
    }
  };

  // Handle editing an existing question
  const handleEditQuestion = (question) => {
    // Find the question's set index and question index
    let questionSetIndex = -1;
    let questionIndex = -1;
    
    // Search through all question sets to find the question
    for (let i = 0; i < quiz.questionSets.length; i++) {
      const qIndex = quiz.questionSets[i].questions.findIndex(q => q.id === question.id);
      if (qIndex !== -1) {
        questionSetIndex = i;
        questionIndex = qIndex;
        break;
      }
    }
    
    if (questionSetIndex !== -1 && questionIndex !== -1) {
      // Update the current set index to show the correct set
      setCurrentSetIndexPersistent(questionSetIndex);
      
      // Call the edit function with the question and its indices
      editQuestion(
        { 
          ...question,
          questionSetIndex,
          setTitle: quiz.questionSets[questionSetIndex].title 
        },
        questionSetIndex,
        questionIndex
      );
    } else {
      console.error('Question not found in any question set');
      toast.error('Could not find the question to edit');
    }
  };

  // Handle adding a new question
  const handleAddQuestion = () => {
    // Ensure we have question sets array
    if (!quiz.questionSets || !Array.isArray(quiz.questionSets)) {
      quiz.questionSets = [];
    }
    
    // If no question sets, create the first one
    if (quiz.questionSets.length === 0) {
      const newSet = {
        id: `set-1`,
        title: 'Set 1', // Simple title format
        questions: [],
      };
      quiz.questionSets.push(newSet);
      setCurrentSetIndexPersistent(0);
      
      // Update the quiz state with the new set
      const updatedQuiz = { ...quiz };
      onUpdateQuiz(updatedQuiz);
      setQuiz(updatedQuiz);
    }
    
    // Ensure currentSetIndex is valid
    const targetQuestionSetIndex = Math.min(Math.max(0, currentSetIndex), quiz.questionSets.length - 1);
    
    // Get the target set
    const targetSet = quiz.questionSets[targetQuestionSetIndex];
    
    // Ensure the target set exists and has a valid ID
    if (!targetSet) {
      console.error('Target question set not found at index:', targetQuestionSetIndex);
      toast.error('Error: Could not find the target question set');
      return;
    }
    
    // Ensure the target set has an ID
    if (!targetSet.id) {
      targetSet.id = `set-${targetQuestionSetIndex + 1}`;
    }
    
    // Use the addQuestion prop to add the question
    if (addQuestion) {
      addQuestion(null, currentSetIndex);
    }
  };

  // Handle deleting a question
  const handleDeleteQuestion = (questionId) => {
    const updatedQuiz = { ...quiz };
    const targetQuestionSetIndex = currentSetIndex >= 0 && currentSetIndex < updatedQuiz.questionSets.length 
      ? currentSetIndex 
      : 0;
      
    if (updatedQuiz.questionSets[targetQuestionSetIndex]?.questions) {
      updatedQuiz.questionSets[targetQuestionSetIndex].questions = 
        updatedQuiz.questionSets[targetQuestionSetIndex].questions.filter(q => q.id !== questionId);
      
      // Update the quiz state
      onUpdateQuiz(updatedQuiz);
      setQuiz(updatedQuiz);
      
      toast.success('Question deleted successfully');
    }
  };

  // Add a new question set
  const addQuestionSet = () => {
    const newSetIndex = quiz.questionSets.length;
    const newSet = {
      id: `set-${newSetIndex + 1}`, // Auto-generated title format
      title: `Set ${newSetIndex + 1}`, // Simple title format
      questions: [],
    };
    
    const updatedQuiz = {
      ...quiz,
      questionSets: [...quiz.questionSets, newSet]
    };
    
    // Update the quiz state
    onUpdateQuiz(updatedQuiz);
    setQuiz(updatedQuiz);
    
    // Switch to the new set and persist the selection
    setCurrentSetIndexPersistent(newSetIndex);
    
    toast.success(`Added ${newSet.title}`);
  };
  
  // Remove a question set
  const removeQuestionSet = (index) => {
    if (quiz.questionSets.length <= 1) {
      toast.error('You must have at least one question set');
      return;
    }
    
    const newSets = [...quiz.questionSets];
    newSets.splice(index, 1);
    
    const updatedQuiz = {
      ...quiz,
      questionSets: newSets
    };
    
    // Update the quiz state
    onUpdateQuiz(updatedQuiz);
    setQuiz(updatedQuiz);
    
    // Adjust current index if needed
    if (currentSetIndex >= newSets.length) {
      setCurrentSetIndex(Math.max(0, newSets.length - 1));
    } else if (currentSetIndex === index) {
      // If removing the current set, move to the previous one if possible
      setCurrentSetIndex(Math.max(0, index - 1));
    }
  };
  
  // Update question set properties
  const updateQuestionSet = (index, updates) => {
    // Don't proceed if the index is invalid
    if (index < 0 || index >= quiz.questionSets.length) return;
    
    const updatedQuiz = { 
      ...quiz,
      questionSets: quiz.questionSets.map((set, i) => 
        i === index ? { ...set, ...updates } : set
      )
    };
    
    // Update the quiz state
    onUpdateQuiz(updatedQuiz);
    setQuiz(updatedQuiz);
  };
console.log(topics)
  // Render the question sets tabs
  const renderQuestionSetTabs = () => {
    return (
      <div className="flex items-center space-x-2 overflow-x-auto pb-2">
        {quiz.questionSets.map((set, index) => (
          <div 
            key={set.id}
            className="cursor-pointer relative transition-all duration-200"
            onClick={() => setCurrentSetIndexPersistent(index)}
          >
            <Badge variant="blue" className={`gap-4 rounded-full px-4 py-2 ${currentSetIndex === index && 'brightness-85 contrast-125 saturate-200'}`}>
              <span className="">{set.title}</span>
              <Badge variant="blue" className={disabled ? "hidden" : "rounded-md p-1 brightness-85 contrast-125 saturate-200"}
                onClick={(e) => {
                  e.stopPropagation();
                  if(quiz.questionSets.length > 1)
                    removeQuestionSet(index);
                  else
                    toast.error('You must have at least one question set');
                }}
              >
                <X className="h-2 w-2" />
              </Badge>
            </Badge>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={addQuestionSet}
          className={disabled ? "hidden" : "flex items-center px-3 py-1 text-sm h-8 shrink-0"}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Set
        </Button>
      </div>
    );
  };

  // Render questions content with Accordion
  const renderQuestionsContent = () => {
    if (!currentSet.questions || currentSet.questions.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
          <p>No questions added yet.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={handleAddQuestion}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add First Question
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <Accordion type="single" collapsible className="w-full space-y-2">
          {currentSet.questions.map((q, index) => (
            <AccordionItem key={q.id} value={`question-${q.id}`} className="border rounded-md py-2 overflow-hidden">
              <div className="flex justify-between items-center pr-4">
                <AccordionTrigger className="hover:no-underline px-4 py-3 hover:bg-muted/50 flex-1 text-left">
                  <div className="flex items-center">
                    <span className="font-medium">{index + 1}. {q.question}</span>
                  </div>
                </AccordionTrigger>
                <div className={disabled ? "hidden" : "flex space-x-2"}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditQuestion(q);
                    }}
                    className="h-8 w-8"
                  >
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteQuestion(q.id);
                    }}
                    className="h-8 w-8 text-destructive hover:text-destructive/90"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                </div>
              </div>
              <AccordionContent className="px-4 pb-4 pt-0">
                <div className="space-y-3">
                  <div className="space-y-2">
                    {q.options.map((option) => {
                      return (
                        <div 
                          key={option.id} 
                          className={`flex items-start p-3 rounded-lg transition-colors ${
                            option.id === q.correctAnswer.id 
                              ? 'bg-[#D3F8DD] text-[#47864D] shadow-sm' 
                              : 'bg-muted/20 hover:bg-muted/30'
                          }`}
                        >
                          <span className="font-medium mr-2 mt-0.5">{String.fromCharCode(65 + q.options.indexOf(option))}.</span>
                          <div className="flex-1">
                            <p className={option.id === q.correctAnswer.id ? 'font-medium ' : ''}>
                              {option.text}
                            </p>
                            {option.id === q.correctAnswer.id && (
                              <div className="flex items-center text-xs mt-1">
                                <CheckCircleIcon className="w-3 h-3 mr-1" />
                                <span>Correct Answer</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-2 flex items-center justify-end">
          <Button
            variant="outline"
            onClick={handleAddQuestion}
            className={disabled ? "hidden" : ""}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </Button>
        </div>
      </div>
    );
  };
  
  // Handle save action - just log the data
  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const quizDataToSave = {
        ...quiz,
        timelimit: 0, // Default to 0
        type: "QUIZ", // Always QUIZ
        questionSets: quiz.questionSets.map(set => ({
          ...set,
          questions: set.questions || []
        }))
      };
      
      // Log the quiz data instead of making API call
      console.log('Quiz data to be saved:', quizDataToSave);
      
      if (onSave) {
        await onSave(quizDataToSave);
      }
      
      toast.success('Quiz data logged successfully');
      
      // Close the editor after successful save
      if (onClose) onClose();
    } catch (error) {
      console.error('Error logging quiz data:', error);
      toast.error('Failed to log quiz data');
    } finally {
      setIsSaving(false);
    }
  };

  // Main component render
  return (
    <Sheet 
        open={editingQuizMaterial !== null} 
        onOpenChange={(open) => !open && onClose()}
      >
        <SheetContent side="right" className="w-screen sm:max-w-[50vw] overflow-y-auto px-10">
          {editingQuizMaterial !== null && (
            <>
              <SheetHeader className="my-6 p-0">
                <SheetTitle className='text-[var(--secondary-font)] font-bold text-2xl'>
                  {`${topics[editingQuizMaterial.topicIndex]?.learningMaterials[editingQuizMaterial.materialIndex]?.title} - Quiz`}
                </SheetTitle>
                <SheetDescription className='text-[var(--secondary-font)]/80 font-light text-sm'>
                  {"Create and manage quiz questions for this learning material."}
                </SheetDescription>
              </SheetHeader>
              
              <div className="w-full h-auto flex-1 relative">
                <div className="space-y-6">
                  <div className="flex justify-between items-center pb-4 border-b">
                      {renderQuestionSetTabs()}
                  </div>
                  
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <TabsList className='gap-4'>
                        <TabsTrigger value="questions" className='px-8 py-5'>Questions</TabsTrigger>
                      </TabsList>
                      <div className="flex space-x-2">
                        <Button
                          onClick={handleSave}
                          disabled={isSaving}
                          className={disabled ? "hidden" : ""}
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving...
                            </>
                          ) : 'Save Quiz'}
                        </Button>
                      </div>
                    </div>
                    
                    <TabsContent value="questions" className="space-y-4">
                      <div className="space-y-4">
                        {renderQuestionsContent()}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
  );
};

export default QuizEditor;